import {Component, inject, Input, ChangeDetectionStrategy} from '@angular/core';
import {Router} from '@angular/router';
import {IconDefinition} from '@fortawesome/fontawesome-svg-core';
import {FaIconComponent} from '@fortawesome/angular-fontawesome';
import {faBookOpen, faCalendarClock, faClipboardList, faScrewdriverWrench} from '@fortawesome/pro-solid-svg-icons';
import {BicButtonComponent} from '@buildinginformationcloud/bic-ui-components';
import {StateConfigService} from '../../../../services/state/state-config.service';

/** Bereiche der myCheck-Ansicht. Die Werte landen als `?tab=` in Lesezeichen. */
export type MyCheckTab = 'own' | 'runs' | 'catalog' | 'schedules';

export function parseMyCheckTab(value: string | null | undefined, fallback: MyCheckTab = 'own'): MyCheckTab {
  return value === 'own' || value === 'runs' || value === 'catalog' || value === 'schedules' ? value : fallback;
}

/**
 * Topbar-Umschalter der myCheck-Ansicht.
 *
 * <p>Navigiert selbst statt per Output: der Umschalter sitzt zwei Ebenen unter der Hülle, und alle
 * Tabs liegen auf derselben Route.
 */
@Component({
  selector: 'app-my-check-tabs',
  standalone: true,
  imports: [BicButtonComponent, FaIconComponent],
  template: `
    <div class="flex flex-row items-center gap-4 flex-1">
      @for (tab of tabs; track tab.key) {
        <bic-button (onClick)="select(tab.key)"
                    [buttonType]="active === tab.key ? 'primary' : 'white'">
          <fa-icon [icon]="tab.icon" iconLeft></fa-icon>
          {{ tab.label }}
        </bic-button>
      }
    </div>
  `,
  // bic-button setzt einen Rahmen nur in der white-Variante. Ohne Ausgleich ist der aktive Tab 2px
  // schmaler und die Nachbarn verrutschen beim Umschalten.
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [`
    :host ::ng-deep bic-button > button:not(.border-gray-300) {
      border: 1px solid transparent;
    }
  `],
})
export class MyCheckTabsComponent {
  @Input({required: true}) active!: MyCheckTab;

  protected readonly tabs: { key: MyCheckTab; label: string; icon: IconDefinition }[] = [
    {key: 'own', label: 'myCheck', icon: faClipboardList},
    {key: 'runs', label: 'Prüfläufe', icon: faScrewdriverWrench},
    {key: 'catalog', label: 'BIC Katalog', icon: faBookOpen},
    {key: 'schedules', label: 'Zeitpläne', icon: faCalendarClock},
  ];

  private router = inject(Router);
  private stateConfigService = inject(StateConfigService);

  protected select(tab: MyCheckTab): void {
    if (tab === this.active) return;
    const projectId = this.stateConfigService.selectedProject?.id;
    if (projectId == null) return;
    void this.router.navigate(['projects', projectId, 'my-checks'], {queryParams: {tab}});
  }
}
